/**
 * CORE-BQ — what the audit actually checked.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * The constitutional rule ends: "A clean audit is a statement about the
 * invariants that were checked and about nothing else."
 *
 * The result shape could not make that statement. It was `{ valid, violations }`
 * with `valid = violations.length === 0`, which made these indistinguishable —
 * byte-identical, measured before this module existed:
 *
 *   every relevant invariant ran and found nothing;
 *   the specialty datasets were never supplied, so nine rules never ran;
 *   the bay dataset was a `[]` placeholder, so two rules examined nothing;
 *   no shop timezone was supplied, so two rules fell back to browser-local time.
 *
 * Only the first is a clean audit. The others are silence being read as health,
 * which is the specific failure CORE-BQ responsibilities 7 and 8 exist to
 * prevent.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY NOT JUST `valid = false`
 * ═══════════════════════════════════════════════════════════════════════════
 * Because "we found a contradiction" and "we did not finish looking" are
 * different facts, and a shop needs both. Collapsing them into one boolean
 * trades one dishonest binary for another. So there are three independent
 * truths here: were violations found, which checks ran, and was the intended
 * coverage complete. A result can truthfully say FAIL *and* incomplete.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NO SCORE
 * ═══════════════════════════════════════════════════════════════════════════
 * There is deliberately no percentage, no grade and no confidence number.
 * "92% covered" cannot be acted on; "BAY_SYNC was not evaluated because the bay
 * dataset was not loaded" can. Coverage names rules.
 */

/* ═══════════════════════════════ the rules ═══════════════════════════════ */

/**
 * The input planes an entry point can be handed. Closed, so a requirement
 * cannot name a dataset that does not exist.
 */
export const INVARIANT_DATASETS = [
  'tickets', 'technicians', 'bays', 'specialtyTasks', 'bayBookings',
] as const;

export type InvariantDataset = (typeof INVARIANT_DATASETS)[number];

/**
 * The TOP-LEVEL columns a rule reads from each plane.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS AUDIT METADATA AND NOT A COMMENT
 * ═══════════════════════════════════════════════════════════════════════════
 * Unit 1 taught the auditor to say "the tickets were never loaded". It could
 * not say "the tickets were loaded, but the column this rule reasons over was
 * not selected" — and a caller that selects ten of a table's columns hands the
 * engine rows where an omitted column is indistinguishable from a NULL one.
 *
 * Measured on the sole production projection before this existed:
 *
 *   NO_OVERLAPS           a real overlap became INVISIBLE, coverage CHECKED
 *   PARKING_LOT_IMMUNITY  a legal rollover child was REPORTED, coverage CHECKED
 *   BAY_SYNC              a correctly-synced tech was REPORTED, coverage CHECKED
 *
 * The first of those is the dangerous direction: the auditor reported health it
 * had not verified, which is precisely what responsibility 7 forbids.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IS AND IS NOT IN SCOPE HERE
 * ═══════════════════════════════════════════════════════════════════════════
 * Presence of a top-level column, and nothing else. Whether the VALUE is null
 * is the rule's business: `assigned_tech_id: null` is a loaded column and a
 * perfectly evaluable row. Whether a present JSON column contains the key a
 * rule wants — `hours['Monday']`, say — is a data-value question, not a
 * projection question, and is deliberately out of scope.
 */
export type InvariantFieldRequirements = Readonly<Partial<Record<InvariantDataset, readonly string[]>>>;

/**
 * WHICH LAYER ACTUALLY REFUSES THIS INVARIANT.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS BELONGS IN THE REGISTRY AND NOWHERE ELSE
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ's fourth responsibility is to "prevent what should be impossible, at
 * the lowest layer capable of refusing it". Some of these rules are prevented
 * that way: the database refuses the write outright, on every path, including
 * background jobs that never load this module. Most are not — they are DETECTED
 * here and nowhere else, which means a violating row can exist.
 *
 * Those are very different promises, and until now nothing said which was
 * which. The knowledge existed only in test-side constants, so a rule could
 * silently change layers and no declaration would contradict it.
 *
 * `database` names the constraint by its live identity, not by a migration
 * filename. That is the binding that makes drift testable: the constraint named
 * here either exists in `pg_constraint` or it does not, and a test can ask.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS NOT
 * ═══════════════════════════════════════════════════════════════════════════
 * Not a general metadata framework, and deliberately two cases rather than a
 * spectrum. "Partially enforced", "enforced by a trigger" and "enforced by an
 * Edge Function" are not modelled, because no rule needs them today and a
 * vocabulary invented ahead of its first use is a vocabulary nobody can check.
 *
 * `javascript` is not a lesser status to be migrated away from on principle.
 * Several of these invariants are cross-row or temporal and cannot become a
 * CHECK; saying so plainly is the honest declaration, not an admission.
 */
export type InvariantEnforcement =
  /** A live database object refuses the write. Named by its deployed identity. */
  | { readonly layer: 'database'; readonly constraint: string }
  /** Detected in this module only. A violating row CAN exist. */
  | { readonly layer: 'javascript' };



/**
 * Every invariant the engine can report, by the id it reports itself as.
 *
 * Keyed by the exact string that appears in `ScheduleViolation.rule`, so a
 * coverage entry and a violation are talking about the same thing and cannot
 * drift apart by renaming.
 *
 * `entryPoint` records which checker owns the rule. `unit` groups rules that
 * share ONE execution — if that execution is skipped or throws, every rule in
 * the group has the same fate, and pretending otherwise would be a guess.
 */
export const INVARIANT_RULES = {
  /* ── schedule: base ── */
  // Unit 4: `bay_assignment` is not a column the tickets table has. The bay
  // identity is `bay_id`, the FK the schema defines and the same one
  // BAY_GLOBAL_OVERLAP and the EXCLUDE constraint use.
  NO_OVERLAPS: { entryPoint: 'schedule', unit: 'NO_OVERLAPS',
    enforcement: { layer: 'javascript' },
    remediation:
      "Two jobs are booked to this technician at the same time. Review both tickets and move one to another time or technician — the board cannot tell you which job should give way.",
    requires: { tickets: ['id', 'status', 'assigned_tech_id', 'bay_id', 'start_time', 'opti_finish'] } },
  // CORE-BQ Unit 5: `assigned_tech_id` is NOT listed. The lower-layer authority
  // `tickets_no_global_bay_overlap` does not reference it, and neither does the
  // verdict — the rule uses it only to phrase the message and to fill
  // diagnostic detail. A field contract names the columns needed to decide
  // TRUTH; listing a message-only column would make a projection that omits it
  // report NOT_EVALUATED_MISSING_FIELD for a rule that could have answered
  // perfectly, which is the opposite of Unit 3's purpose.
  BAY_GLOBAL_OVERLAP: { entryPoint: 'schedule', unit: 'BAY_GLOBAL_OVERLAP',
    enforcement: { layer: 'database', constraint: 'tickets_no_global_bay_overlap' },
    remediation:
      "Two vehicles are booked into this bay for the same window. Review the conflicting tickets and move one to a bay that is free.",
    requires: { tickets: ['id', 'status', 'deleted_at', 'bay_id', 'start_time', 'opti_finish'] } },
  // Unit 9: both advisories now read the lifecycle columns, so both must
  // declare them. `deleted_at` is the tombstone half of the liveness test, and
  // WAITER_EOD had never listed `status` at all because it had never consulted
  // one. A caller that supplies neither is now told the rule was not evaluated,
  // which is the point: an advisory that cannot tell live work from finished
  // work should not be reporting either way.
  NO_PAST_TIMES: { entryPoint: 'schedule', unit: 'NO_PAST_TIMES',
    enforcement: { layer: 'javascript' },
    remediation:
      "This job's start time has already gone by. Reschedule it to a time that is still ahead, or close it out if the work is actually finished.",
    requires: { tickets: ['id', 'status', 'deleted_at', 'start_time', 'scheduled_date'] } },
  WAITER_EOD: { entryPoint: 'schedule', unit: 'WAITER_EOD',
    enforcement: { layer: 'javascript' },
    remediation:
      "This waiter is promised their vehicle after their technician's shift ends. Either reset the promise time with the customer, or move the work to someone still on shift.",
    requires: { tickets: ['id', 'status', 'deleted_at', 'type', 'opti_finish', 'assigned_tech_id'],
    technicians: ['id', 'name', 'hours', 'shift_end'] } },
  PARKING_LOT_IMMUNITY: { entryPoint: 'schedule', unit: 'PARKING_LOT_IMMUNITY',
    enforcement: { layer: 'database', constraint: 'tickets_parking_lot_immunity' },
    remediation:
      "A parked vehicle is still holding a technician. Release the assignment, or move the vehicle back into a bay if work is continuing.",
    requires: { tickets: ['id', 'parking_lot', 'assigned_tech_id', 'status', 'rollover_of_ticket_id', 'parked_with_chain_plan'] } },
  // Unit 4: same identity correction. `name` is no longer read at all.
  BAY_TYPE_REQUIREMENT: { entryPoint: 'schedule', unit: 'BAY_TYPE_REQUIREMENT',
    enforcement: { layer: 'javascript' },
    remediation:
      "This job needs a lift and is sitting in a flat bay. Move it to a bay with a lift before work starts.",
    requires: { tickets: ['id', 'bay_id', 'requires_lift'],
    bays: ['id', 'bay_type'] } },
  // Unit 4: the rule now compares the technician's DERIVED bay-type label
  // multiset against the types of the bays assigned to them, so `bay_type` is
  // load-bearing and the two `name` columns are not read at all. `name` survives
  // only in a violation message, behind a fallback to the id.
  BAY_SYNC: { entryPoint: 'schedule', unit: 'BAY_SYNC',
    enforcement: { layer: 'javascript' },
    remediation:
      "The bay record and the technician record disagree about who is assigned. Re-save the bay assignment in Shop Floor so both agree.",
    requires: { technicians: ['id', 'bay_assignments'],
    bays: ['assigned_tech_id', 'bay_type'] } },

  /* ── schedule: specialty S1–S9 ── */
  SPECIALTY_TASK_BOOKING: { entryPoint: 'schedule', unit: 'SPECIALTY_TASK_BOOKING',
    enforcement: { layer: 'javascript' },
    remediation:
      "This specialty task has no bay booking. Book a specialty bay for it, or cancel the task if it is no longer wanted.",
    requires: { specialtyTasks: ['id', 'status'], bayBookings: ['specialty_task_id'] } },
  SPECIALTY_BAY_DOUBLE_BOOKING: { entryPoint: 'schedule', unit: 'SPECIALTY_BAY_DOUBLE_BOOKING',
    enforcement: { layer: 'javascript' },
    remediation:
      "Two bookings overlap in this specialty bay. Review both and move one to a free window.",
    requires: { bayBookings: ['status', 'bay_id', 'start_time', 'end_time'] } },
  SPECIALTY_BAY_TYPE: { entryPoint: 'schedule', unit: 'SPECIALTY_BAY_TYPE',
    enforcement: { layer: 'javascript' },
    remediation:
      "This specialty work is scheduled in a bay that cannot do it. Move it to an alignment or ADAS bay.",
    requires: { specialtyTasks: ['id', 'bay_id', 'bay_type'],
    bays: ['id', 'name', 'bay_category'] } },
  // S4 emits two rule ids from one pass — a sub-ticket can be wrong about its
  // primary, or the primary can be missing entirely.
  SPECIALTY_SUB_REFERENCE: { entryPoint: 'schedule', unit: 'SPECIALTY_SUB_REFERENCE',
    enforcement: { layer: 'javascript' },
    remediation:
      "This task points at a sub-ticket that no longer exists. Re-link it to the right ticket, or clear the stale reference.",
    requires: { specialtyTasks: ['id', 'primary_ticket_id', 'sub_ticket_id'],
    tickets: ['id'] } },
  SPECIALTY_PRIMARY_REFERENCE: { entryPoint: 'schedule', unit: 'SPECIALTY_SUB_REFERENCE',
    enforcement: { layer: 'javascript' },
    remediation:
      "This task is not linked to a visit that exists. Link it to the ticket it belongs to before working it.",
    requires: { specialtyTasks: ['id', 'primary_ticket_id', 'sub_ticket_id'],
    tickets: ['id'] } },
  SPECIALTY_TECH_PROFICIENCY: { entryPoint: 'schedule', unit: 'SPECIALTY_TECH_PROFICIENCY',
    enforcement: { layer: 'javascript' },
    remediation:
      "The assigned technician is below the proficiency your shop requires for this work. Reassign it, or review the requirement in Shop Profile if it is set too high.",
    requires: { specialtyTasks: ['id', 'bay_type', 'tech_id'],
    technicians: ['id', 'name', 'proficiencies'] } },
  SPECIALTY_TECH_SHIFT: { entryPoint: 'schedule', unit: 'SPECIALTY_TECH_SHIFT',
    enforcement: { layer: 'javascript' },
    remediation:
      "This task is scheduled outside the assigned technician's shift. Move it inside the shift, or assign someone who is working then.",
    requires: { specialtyTasks: ['id', 'tech_id', 'scheduled_start', 'scheduled_end'],
    technicians: ['id', 'name', 'hours', 'shift_start', 'shift_end'] } },
  SPECIALTY_BOOKING_OVERLAP: { entryPoint: 'schedule', unit: 'SPECIALTY_BOOKING_OVERLAP',
    enforcement: { layer: 'javascript' },
    remediation:
      "This technician has two specialty bookings that overlap. Review both and move one.",
    requires: { bayBookings: ['status', 'tech_id', 'start_time', 'end_time'] } },
  SPECIALTY_STATUS: { entryPoint: 'schedule', unit: 'SPECIALTY_STATUS',
    enforcement: { layer: 'javascript' },
    remediation:
      "This task's status is not one the system recognises, which usually means it was written by an older import. Review the task before working it.",
    requires: { specialtyTasks: ['id', 'status'] } },
  SPECIALTY_ACTUAL_MINUTES: { entryPoint: 'schedule', unit: 'SPECIALTY_ACTUAL_MINUTES',
    enforcement: { layer: 'javascript' },
    remediation:
      "This task is finished but no actual time was recorded. Add the time so reporting and estimates stay honest.",
    requires: { specialtyTasks: ['id', 'status', 'actual_minutes'] } },

  /* ── lifecycle ──
   * All eleven are evaluated inside ONE pass over the ticket set, so they share
   * a unit. Splitting them would mean reordering the emitted violations, which
   * is a behaviour change this unit is not permitted to make — and it would buy
   * nothing, because they have identical prerequisites and would always share a
   * status anyway. */
  LIFECYCLE_STATUS_ENUM: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_status_check' },
    remediation:
      "This ticket's status is not one the system recognises. Review the ticket before continuing work on it.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_TIMER_ENUM: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_timer_status_check' },
    remediation:
      "This ticket's timer state is not one the system recognises. Review the ticket's timer before continuing.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_PAUSED_TIMER: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_active_paused_pickup_state' },
    remediation:
      "This ticket is paused but its timer does not agree. Review the timer before continuing work — the recorded time may be wrong.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_PAUSED_REASON: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'javascript' },
    remediation:
      "This ticket is paused with no reason recorded. Add the reason so the board explains itself to the next person who looks.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_ACTIVE_STALE_PAUSE: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'javascript' },
    remediation:
      "This ticket is active but still carries pause details from earlier. Review the ticket state before continuing.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_COMPLETED_TIMER: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_terminal_state' },
    remediation:
      "This ticket is complete but its timer is still running or paused. Review it — the time recorded against this job may be wrong.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_COMPLETED_BAY: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_terminal_state' },
    remediation:
      "This ticket is complete but still holds a bay. Record where the vehicle actually went so the bay is released.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_TERMINAL_BAY: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_terminal_state' },
    remediation:
      "This ticket is closed but still holds a bay. Record where the vehicle actually is so the bay is released.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_TERMINAL_TIMER: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_terminal_state' },
    remediation:
      "This ticket is closed but its timer is still running or paused. Review it — the time recorded against this job may be wrong.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_PICKED_UP_LOCATION: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_active_paused_pickup_state' },
    remediation:
      "This vehicle is marked picked up but still has a location on the floor. Review the ticket — either it has gone or it has not.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
  LIFECYCLE_RUNNING_STATUS: { entryPoint: 'lifecycle', unit: 'LIFECYCLE_SCAN',
    enforcement: { layer: 'database', constraint: 'tickets_lifecycle_active_paused_pickup_state' },
    remediation:
      "A timer is running on a ticket that is not active. Review the ticket's status and its timer before continuing work.",
    requires: { tickets: ['id', 'status', 'timer_status', 'pause_reason', 'paused_at',
      'bay_id', 'parking_spot_id', 'parking_lot', 'picked_up_at', 'deleted_at'] } },
} as const;

export type InvariantRuleId = keyof typeof INVARIANT_RULES;
export type InvariantEntryPoint = (typeof INVARIANT_RULES)[InvariantRuleId]['entryPoint'];

export const INVARIANT_RULE_IDS = Object.keys(INVARIANT_RULES) as InvariantRuleId[];

export function invariantRulesFor(entryPoint: InvariantEntryPoint): InvariantRuleId[] {
  return INVARIANT_RULE_IDS.filter((id) => INVARIANT_RULES[id].entryPoint === entryPoint);
}

/** Which layer refuses this invariant. Every registered rule declares one. */
export function enforcementOf(rule: InvariantRuleId): InvariantEnforcement {
  return INVARIANT_RULES[rule].enforcement as InvariantEnforcement;
}

/**
 * The safest next step a person can take about this contradiction.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Slice D — WHY THIS LIVES IN THE REGISTRY
 * ═══════════════════════════════════════════════════════════════════════════
 * A remediation catalogue kept beside the rules drifts away from them: a rule
 * added later has no guidance and nobody notices, and a rule removed leaves
 * advice about something that can no longer happen. Declaring it here makes the
 * rule and its guidance the same object, and the type below makes a rule
 * without guidance fail to compile.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS TEXT IS ALLOWED TO SAY
 * ═══════════════════════════════════════════════════════════════════════════
 * Guidance, and never an instruction the auditor cannot justify. Most of these
 * contradictions have SEVERAL valid resolutions — an overlap can be fixed by
 * moving either job, and the board has no way to know which one should give
 * way. So the wording names what needs deciding and leaves the decision with
 * the person, rather than manufacturing a confidence the engine does not have.
 *
 * Nothing here is executable. It is a sentence to read.
 */
export function remediationFor(rule: InvariantRuleId): string {
  return INVARIANT_RULES[rule].remediation as string;
}

/**
 * Compile-time proof that every registered rule carries guidance.
 *
 * A runtime test asserts the same thing, but this fails earlier and cannot be
 * skipped: adding a rule without `remediation` stops the build rather than
 * shipping a violation the shop is shown with nothing to do about it.
 */
/** Collects the ids of any rules lacking guidance. Empty means `never`. */
type RulesMissingRemediation = {
  [K in InvariantRuleId]: (typeof INVARIANT_RULES)[K] extends { remediation: string }
    ? never : K;
}[InvariantRuleId];

/**
 * Resolves only when the set above is empty. If a rule is added without
 * guidance, `RulesMissingRemediation` becomes that rule's id, the constraint
 * fails, and the build names the offending rule.
 *
 * Written this way on purpose: the obvious version — building an object of
 * `true`s and asserting its type — type-checks nothing, because the `as` cast
 * needed to seed the reduce suppresses the very error it was meant to raise.
 * Measured: deleting a rule's guidance left that version completely silent.
 */
type AssertNoneMissing<T extends never> = T;
export type RemediationCompleteness = AssertNoneMissing<RulesMissingRemediation>;

/**
 * The rules this registry claims a database object refuses, paired with the
 * constraint claimed. A test can hand these straight to `pg_constraint` and ask
 * whether the claim is true — which is the whole point of naming the constraint
 * rather than describing it.
 */
export function databaseEnforcedRules(): { rule: InvariantRuleId; constraint: string }[] {
  return INVARIANT_RULE_IDS.flatMap((rule) => {
    const e = enforcementOf(rule);
    return e.layer === 'database' ? [{ rule, constraint: e.constraint }] : [];
  });
}

/** Rules detected here and nowhere else — a violating row CAN exist for these. */
export function javascriptOnlyRules(): InvariantRuleId[] {
  return INVARIANT_RULE_IDS.filter((rule) => enforcementOf(rule).layer === 'javascript');
}

/** The distinct constraints the registry depends on, for a single liveness query. */
export function claimedConstraintNames(): string[] {
  return [...new Set(databaseEnforcedRules().map((r) => r.constraint))].sort();
}

/**
 * The union of what a group of rules reads. Rules that share one execution
 * share its fate, so they share its prerequisites.
 */
export function requiredFieldsFor(
  rules: readonly InvariantRuleId[],
): Readonly<Partial<Record<InvariantDataset, string[]>>> {
  const out: Partial<Record<InvariantDataset, string[]>> = {};
  for (const rule of rules) {
    const requires = INVARIANT_RULES[rule].requires as InvariantFieldRequirements;
    for (const [dataset, fields] of Object.entries(requires) as [InvariantDataset, readonly string[]][]) {
      const bucket = out[dataset] ?? (out[dataset] = []);
      for (const f of fields) if (!bucket.includes(f)) bucket.push(f);
    }
  }
  return out;
}

/**
 * Which required columns are absent from a loaded dataset.
 *
 * OWN-PROPERTY, never truthiness. `null`, `false`, `0`, `''` and `[]` are all
 * legitimate loaded values, and a rule handed `rollover_of_ticket_id: null`
 * knows the row is not a rollover child — which is a completely different fact
 * from never receiving the column.
 *
 * A dataset that was not loaded returns nothing: the dataset-level gate owns
 * that case, and reporting both would say the same thing twice in two
 * vocabularies. An EMPTY loaded dataset is satisfied — there is no row whose
 * omitted column could change any rule's answer, and calling it incomplete
 * would undo Unit 1's loaded-empty semantics.
 *
 * If ANY row is missing the column the requirement is unmet. Not most rows, not
 * a proportion: a rule that skipped the one row that mattered did not run.
 */
export function missingFieldsIn(
  rows: readonly Record<string, unknown>[] | null | undefined,
  required: readonly string[],
): string[] {
  if (!Array.isArray(rows) || rows.length === 0 || required.length === 0) return [];
  const missing: string[] = [];
  for (const field of required) {
    const absent = rows.some(
      (row) => !row || typeof row !== 'object'
        || !Object.prototype.hasOwnProperty.call(row, field),
    );
    if (absent) missing.push(field);
  }
  return missing;
}

/* ═══════════════════════════ coverage vocabulary ═══════════════════════════ */

/**
 * Closed. A status outside this set would be a coverage claim nobody defined.
 *
 * `NOT_APPLICABLE` is declared and currently has NO producer, deliberately.
 * It means the invariant genuinely does not apply to the shop being evaluated —
 * not that the caller forgot to load something. No rule in the present engine is
 * shop-conditional in that way, and inventing a producer to make the vocabulary
 * look used would be exactly the kind of decoration this control exists to
 * refuse. It is here so the distinction stays available and named when a
 * conditional rule arrives.
 */
export type InvariantCoverageStatus =
  /** Ran, with every input the implementation needs. */
  | 'CHECKED'
  /**
   * Ran, but in a documented reduced mode because shop context was missing.
   * The rule executed; the SHOP-RELATIVE invariant did not. Counted as
   * incomplete coverage — see `isCompleteStatus`.
   */
  | 'CHECKED_DEGRADED'
  /** Genuinely does not apply to this shop's state. */
  | 'NOT_APPLICABLE'
  /** A dataset the rule needs was never supplied. */
  | 'NOT_EVALUATED_MISSING_INPUT'
  /**
   * The dataset arrived, without a column the rule reads.
   *
   * Deliberately its own status rather than another `MISSING_INPUT` reason,
   * because the two call for different fixes — load the plane, versus widen the
   * projection — and a status a reader has to decode a reason code to
   * understand is a status that gets skimmed.
   */
  | 'NOT_EVALUATED_MISSING_FIELD'
  /** The rule threw. Never a pass. */
  | 'NOT_EVALUATED_ERROR';

/**
 * Closed reason codes. Deliberately not free text and never an exception
 * message: this structure is safe to log and safe to surface, and a stack trace
 * or a quoted row would make it neither.
 */
export type InvariantSkipReason =
  | 'tickets_not_loaded'
  | 'technicians_not_loaded'
  | 'bays_not_loaded'
  | 'specialty_tasks_not_loaded'
  | 'bay_bookings_not_loaded'
  | 'shop_timezone_missing'
  | 'required_field_not_loaded'
  | 'check_threw';

/** Which columns were absent, and from where. Names only — never a row value. */
export interface InvariantMissingFields {
  readonly dataset: InvariantDataset;
  readonly fields: readonly string[];
}

export interface InvariantCoverageEntry {
  readonly rule: InvariantRuleId;
  readonly status: InvariantCoverageStatus;
  /** Violations this rule produced. Zero for anything that did not run. */
  readonly violationCount: number;
  readonly reason?: InvariantSkipReason;
  /**
   * Present only for NOT_EVALUATED_MISSING_FIELD. Column names and dataset
   * names are structural facts about the QUERY, not about anyone's data, so
   * they are safe to log and safe to surface — which is the whole point of
   * naming them instead of reporting a number.
   */
  readonly missingFields?: readonly InvariantMissingFields[];
  /**
   * CORE-BQ Unit 6. Present ONLY on entries that actually executed — CHECKED
   * or CHECKED_DEGRADED.
   *
   * A rule whose dataset was never loaded, or whose column was not selected,
   * has a more immediate problem than the shape of its population, and
   * reporting both would bury the one the caller has to fix first. Missing
   * input and missing field keep precedence; row scope describes runs.
   */
  readonly rowScope?: InvariantRuleRowScope;
}

export interface InvariantCoverage {
  /**
   * Every rule this entry point intends to evaluate was actually evaluated,
   * with the inputs it needs, over a population the caller vouched for.
   *
   * False the moment one was skipped, degraded, threw — or ran against rows
   * that were filtered or undeclared. CORE-BQ Unit 6 added the second half:
   * before it, a rule could execute cleanly over an admitted subset and still
   * contribute to a complete audit.
   */
  readonly complete: boolean;
  readonly checks: readonly InvariantCoverageEntry[];
}

/* ═════════════════════════════ row scope ═════════════════════════════════ */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 6 — POPULATION COMPLETENESS IS NOT EXECUTION COMPLETENESS
 * ═══════════════════════════════════════════════════════════════════════════
 * Units 1 and 3 taught the auditor to say whether a rule RAN and whether the
 * COLUMNS it reads arrived. Neither says anything about the ROWS. A caller can
 * hand over a perfectly projected, fully populated subset, and every rule will
 * execute cleanly over it and report CHECKED — while the contradiction sits in
 * a row the query never selected.
 *
 * Unit 5 proved that is the live situation, not a hypothetical: the sole
 * production caller loads `status IN ('Active','Paused')`, and the database
 * authority for BAY_GLOBAL_OVERLAP also governs Blocked tickets.
 *
 * So this is a SECOND, ORTHOGONAL axis. `CHECKED` keeps exactly the meaning
 * Unit 1 gave it — the rule executed against the rows supplied — and row scope
 * is recorded beside it. A rule can legitimately be
 *
 *     status  = CHECKED_DEGRADED   (no shop timezone)
 *     scope   = FILTERED           (status subset)
 *
 * and both facts must survive; collapsing them into one status would force a
 * choice between two true statements.
 */
export const INVARIANT_ROW_SCOPE_STATUSES = ['EXHAUSTIVE', 'FILTERED', 'UNKNOWN'] as const;

/**
 * EXHAUSTIVE is a CLAIM the caller makes, and it is a strong one: there is no
 * omitted row within this audit universe whose presence could change a rule's
 * answer. It does not mean "every row in the table" — a synthetic fixture
 * universe can honestly be exhaustive — but it does mean the caller has thought
 * about the population, not just the projection.
 *
 * FILTERED means the caller knowingly supplied a subset.
 *
 * UNKNOWN means nothing was claimed. It is the default, deliberately: a caller
 * that forgets to describe its population must never be rewarded with the
 * strongest reading of it.
 */
export type InvariantRowScopeStatus = (typeof INVARIANT_ROW_SCOPE_STATUSES)[number];

/**
 * Closed structural reasons — the same discipline as `InvariantSkipReason`.
 * These describe the QUERY, never a row: no dates, no ids, no counts, no free
 * text. Safe to log, safe to surface.
 */
export const INVARIANT_ROW_SCOPE_REASONS = [
  /** A subset of the status domain the rule's authority governs. */
  'status_subset',
  /** Sliced on `scheduled_date`, which is not what interval rules key on. */
  'scheduled_date_subset',
  /** Only rows referenced by an already-filtered dataset were fetched. */
  'referenced_ids_only',
  /** Some other deliberate caller-side narrowing. */
  'caller_filtered',
  /** Nothing was declared. Never a claim — the fail-safe. */
  'scope_not_declared',
] as const;

export type InvariantRowScopeReason = (typeof INVARIANT_ROW_SCOPE_REASONS)[number];

export interface InvariantDatasetRowScope {
  readonly status: InvariantRowScopeStatus;
  readonly reasons?: readonly InvariantRowScopeReason[];
}

/**
 * A stronger, NARROWER claim than the dataset-level one: this specific rule's
 * population is complete, even though the dataset as a whole is a subset.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 7 — WHY PER-RULE AND NOT PER-DATASET
 * ═══════════════════════════════════════════════════════════════════════════
 * One `tickets` array feeds every ticket-consuming rule, but the rules do not
 * share a population. `BAY_GLOBAL_OVERLAP` can only be reached by rows holding
 * a `bay_id`, and the live `tickets_lifecycle_terminal_state` forbids a
 * Completed / Cancelled / Deleted row from holding one — so loading every
 * Active / Paused / Blocked ticket in the shop IS that rule's whole universe.
 * The same array was emphatically NOT the whole universe of `NO_PAST_TIMES`
 * when this was written, because that rule ranged over terminal rows as well.
 *
 * CORE-BQ Unit 9 changed the fact rather than the claim. It established that an
 * advisory has no business speaking about a terminal or tombstoned row, and
 * once both advisories decline to, their domain IS the non-terminal statuses —
 * which is what the production scan loads. The population claim became true
 * because the domain was repaired, not because the bar was lowered.
 *
 * Flipping the dataset to EXHAUSTIVE would still hand the stronger claim to
 * every rule that touches tickets, several of which have no established domain
 * even now. That is the overclaim Unit 6 exists to prevent, so the dataset
 * stays FILTERED and the rules whose domains were actually proven are named
 * individually.
 *
 * Only datasets a rule genuinely consumes are consulted, so an override naming
 * an irrelevant plane changes nothing, and an override for one rule cannot
 * reach another. Rule ids are `InvariantRuleId`, so an unregistered one will
 * not compile.
 */
export type InvariantRowScopeOverrides = Readonly<Partial<Record<
  InvariantRuleId,
  Readonly<Partial<Record<InvariantDataset, InvariantDatasetRowScope>>>
>>>;

/** What the caller says about the populations it supplied. Every key optional. */
export type InvariantInputRowScope =
  Readonly<Partial<Record<InvariantDataset, InvariantDatasetRowScope>>> & {
    /**
     * Per-rule claims that override the dataset-level one. Deliberately not a
     * global `assumeExhaustive` switch: every stronger claim has to name the
     * rule it applies to, so it can be argued about one rule at a time.
     */
    readonly ruleOverrides?: InvariantRowScopeOverrides;
  };

/** Every dataset resolved, with undeclared ones filled in as UNKNOWN. */
export type ResolvedInvariantInputRowScope =
  Readonly<Record<InvariantDataset, InvariantDatasetRowScope>>;

export interface InvariantRuleDatasetScope {
  readonly dataset: InvariantDataset;
  readonly status: InvariantRowScopeStatus;
  readonly reasons?: readonly InvariantRowScopeReason[];
}

export interface InvariantRuleRowScope {
  /** Every dataset this rule CONSUMES was claimed exhaustive. */
  readonly complete: boolean;
  readonly datasets: readonly InvariantRuleDatasetScope[];
}

/**
 * The fail-safe. An omitted declaration resolves here, never to EXHAUSTIVE.
 *
 * This is the single most load-bearing line in the unit: if forgetting to
 * declare scope produced a complete-looking audit, the contract would be
 * decoration.
 */
const UNDECLARED_SCOPE: InvariantDatasetRowScope = Object.freeze({
  status: 'UNKNOWN' as const,
  reasons: Object.freeze(['scope_not_declared' as const]),
});

export function resolveInputRowScope(
  declared?: InvariantInputRowScope | null,
): ResolvedInvariantInputRowScope {
  const out = {} as Record<InvariantDataset, InvariantDatasetRowScope>;
  for (const dataset of INVARIANT_DATASETS) {
    out[dataset] = declared?.[dataset] ?? UNDECLARED_SCOPE;
  }
  return out;
}

/** The per-rule claims, if any. Absent is the norm and means "no override". */
export function rowScopeOverridesOf(
  declared?: InvariantInputRowScope | null,
): InvariantRowScopeOverrides {
  return declared?.ruleOverrides ?? {};
}

/**
 * A rule's population truth, derived ONLY from the datasets it actually reads.
 *
 * The dataset list comes from `INVARIANT_RULES[rule].requires` — the same
 * metadata Unit 3 uses for columns. There is deliberately no second registry
 * saying which planes a rule consumes: two lists of the same fact drift, and
 * the drifting one is always the one nobody is looking at.
 *
 * A rule is not penalised for a plane it does not read. BAY_GLOBAL_OVERLAP
 * consumes only `tickets`, so a filtered technician dataset says nothing about
 * it.
 */
export function deriveRuleRowScope(
  rule: InvariantRuleId,
  scope: ResolvedInvariantInputRowScope,
  /**
   * CORE-BQ Unit 7. A per-rule claim wins over the dataset-level one for THIS
   * rule only. Iterating the consumed datasets is what keeps it narrow: an
   * override naming a plane the rule does not read is simply never reached.
   */
  overrides: InvariantRowScopeOverrides = {},
): InvariantRuleRowScope {
  const consumed = Object.keys(requiredFieldsFor([rule])) as InvariantDataset[];
  const ruleOverride = overrides[rule];
  const datasets = consumed.map((dataset) => {
    const declared = ruleOverride?.[dataset] ?? scope[dataset];
    return {
      dataset,
      status: declared.status,
      ...(declared.reasons && declared.reasons.length > 0
        ? { reasons: declared.reasons } : {}),
    };
  });
  return {
    // Conservative by construction: anything short of an explicit EXHAUSTIVE
    // claim on every consumed plane leaves the population unproven.
    complete: datasets.length > 0 && datasets.every((d) => d.status === 'EXHAUSTIVE'),
    datasets,
  };
}

/** A verdict is derived, never assigned — see `deriveInvariantVerdict`. */
export type InvariantVerdict = 'PASS' | 'FAIL' | 'INCOMPLETE';

/** Only these two say "the intended check happened". */
export function isCompleteStatus(status: InvariantCoverageStatus): boolean {
  return status === 'CHECKED' || status === 'NOT_APPLICABLE';
}

/**
 * The one derivation.
 *
 * FAIL outranks INCOMPLETE because a found contradiction is the more actionable
 * fact — but the coverage object still records the incompleteness beside it, so
 * "we found a problem AND we did not finish looking" survives intact. A caller
 * that wants the second fact reads `coverage.complete`; it is not allowed to
 * compute the verdict itself, because two callers computing it differently is
 * how the honest result stops being one.
 */
export function deriveInvariantVerdict(
  violationCount: number,
  coverage: InvariantCoverage,
): InvariantVerdict {
  if (violationCount > 0) return 'FAIL';
  return coverage.complete ? 'PASS' : 'INCOMPLETE';
}

/* ═══════════════════════════ the execution recorder ═══════════════════════ */

/**
 * How a rule's prerequisites were met, expressed at the call site.
 *
 * `missing` wins over `degraded`: a rule with no dataset did not run at all, so
 * describing it as a reduced-mode run would overstate what happened.
 */
export interface InvariantGate {
  /** The rule cannot run; this dataset was not supplied. */
  readonly missing?: InvariantSkipReason;
  /**
   * The datasets arrived without columns the rule reads. Outranked by
   * `missing`: a plane that was never loaded has no columns to be missing, and
   * saying both would say the same thing twice in two vocabularies.
   */
  readonly missingFields?: readonly InvariantMissingFields[];
  /** The rule can run, but only in a documented reduced mode. */
  readonly degraded?: InvariantSkipReason;
  /** The rule genuinely does not apply here. */
  readonly notApplicable?: true;
}

export interface InvariantRecorder {
  /**
   * Execute one unit of checking and record what happened to every rule it
   * owns.
   *
   * Rules are passed as a group because several units emit more than one rule
   * id, and because a unit that is skipped or throws takes all of its rules
   * with it. Passing them here — rather than declaring them somewhere else — is
   * what makes it hard to add a rule and forget its coverage: the ids are
   * `InvariantRuleId`, so an unregistered one will not compile.
   */
  run(
    rules: readonly InvariantRuleId[],
    gate: InvariantGate,
    check: () => import('./invariants').ScheduleViolation[],
  ): void;
  /** Everything collected so far, in execution order. */
  readonly violations: import('./invariants').ScheduleViolation[];
  /** Seal the result. Throws if a registered rule for this entry point is unaccounted for. */
  finish(entryPoint: InvariantEntryPoint): {
    valid: boolean;
    verdict: InvariantVerdict;
    violations: import('./invariants').ScheduleViolation[];
    coverage: InvariantCoverage;
  };
}

export function createInvariantRecorder(
  /**
   * CORE-BQ Unit 6. What the caller claims about the POPULATIONS it supplied.
   *
   * Optional, and omission is not neutral: it resolves to UNKNOWN on every
   * plane, which cannot contribute to a complete audit. That is the fail-safe —
   * a caller earns a scope-complete result by describing its query, never by
   * staying silent about it.
   */
  declaredRowScope?: InvariantInputRowScope | null,
): InvariantRecorder {
  const violations: import('./invariants').ScheduleViolation[] = [];
  const entries: InvariantCoverageEntry[] = [];
  const rowScope = resolveInputRowScope(declaredRowScope);
  const rowScopeOverrides = rowScopeOverridesOf(declaredRowScope);

  const record = (
    rules: readonly InvariantRuleId[],
    status: InvariantCoverageStatus,
    reason: InvariantSkipReason | undefined,
    produced: import('./invariants').ScheduleViolation[],
    missingFields?: readonly InvariantMissingFields[],
  ) => {
    // Row scope describes a run. A rule that never reached its inputs has a
    // more immediate problem, and saying both would bury the actionable one.
    const executed = status === 'CHECKED' || status === 'CHECKED_DEGRADED';
    for (const rule of rules) {
      entries.push({
        rule,
        status,
        ...(missingFields && missingFields.length > 0 ? { missingFields } : {}),
        // Counted per rule rather than per unit, so a multi-rule pass reports
        // which of its rules actually fired.
        violationCount: produced.filter((v) => v.rule === rule).length,
        ...(reason ? { reason } : {}),
        ...(executed
          ? { rowScope: deriveRuleRowScope(rule, rowScope, rowScopeOverrides) }
          : {}),
      });
    }
  };

  return {
    violations,
    run(rules, gate, check) {
      if (gate.missing) {
        record(rules, 'NOT_EVALUATED_MISSING_INPUT', gate.missing, []);
        return;
      }
      if (gate.missingFields && gate.missingFields.length > 0) {
        // Discovered BEFORE the rule is called. A missing column is known
        // incomplete input, not an exception — letting the rule run and throw
        // would report it as an error, and letting it run and NOT throw is how
        // an under-evaluated rule came to look clean in the first place.
        record(rules, 'NOT_EVALUATED_MISSING_FIELD', 'required_field_not_loaded',
          [], gate.missingFields);
        return;
      }
      if (gate.notApplicable) {
        record(rules, 'NOT_APPLICABLE', undefined, []);
        return;
      }
      let produced: import('./invariants').ScheduleViolation[];
      try {
        produced = check();
      } catch {
        // Never a pass. The exception itself is deliberately not carried into
        // the result: this structure is logged and surfaced, and a message from
        // a throwing rule can quote operational data. Development diagnostics
        // stay in the console below.
        record(rules, 'NOT_EVALUATED_ERROR', 'check_threw', []);
        if (typeof console !== 'undefined') {
          console.error('[CORE-BQ] invariant check threw', rules.join(','));
        }
        return;
      }
      violations.push(...produced);
      record(rules, gate.degraded ? 'CHECKED_DEGRADED' : 'CHECKED', gate.degraded, produced);
    },
    finish(entryPoint) {
      // A rule that is registered for this entry point but never reached the
      // recorder is the failure mode this whole module exists to prevent — a
      // silent hole in the audit. It is a programming error, so it throws here
      // rather than being reported as coverage.
      const seen = new Set(entries.map((e) => e.rule));
      const missing = invariantRulesFor(entryPoint).filter((r) => !seen.has(r));
      if (missing.length > 0) {
        throw new Error(
          `[CORE-BQ] ${entryPoint} audit did not account for: ${missing.join(', ')}`,
        );
      }
      const coverage: InvariantCoverage = {
        // Both axes. A rule that executed cleanly over an admitted subset is
        // not evidence the invariant holds — it is evidence it holds among the
        // rows someone chose to look at.
        complete: entries.every((e) =>
          isCompleteStatus(e.status) && (e.rowScope ? e.rowScope.complete : true)),
        checks: entries,
      };
      return {
        valid: violations.length === 0,
        verdict: deriveInvariantVerdict(violations.length, coverage),
        violations,
        coverage,
      };
    },
  };
}

/* ═══════════════════════════ reading a coverage ═══════════════════════════ */

/** Rules that did not produce the check they were supposed to. Safe to log. */
export function skippedInvariantRules(
  coverage: InvariantCoverage,
): readonly InvariantCoverageEntry[] {
  return coverage.checks.filter((c) => !isCompleteStatus(c.status));
}

/** Executed, but over a population the caller admitted was a subset. */
export function filteredScopeRules(
  coverage: InvariantCoverage,
): readonly InvariantCoverageEntry[] {
  return coverage.checks.filter((c) => c.rowScope
    && c.rowScope.datasets.some((d) => d.status === 'FILTERED'));
}

/** Executed, with nothing claimed about the population at all. */
export function unknownScopeRules(
  coverage: InvariantCoverage,
): readonly InvariantCoverageEntry[] {
  return coverage.checks.filter((c) => c.rowScope
    && c.rowScope.datasets.some((d) => d.status === 'UNKNOWN'));
}

/** Ran, AND over a population vouched for. The strongest thing an audit says. */
export function scopeCompleteRules(
  coverage: InvariantCoverage,
): readonly InvariantCoverageEntry[] {
  return coverage.checks.filter((c) =>
    isCompleteStatus(c.status) && c.rowScope?.complete === true);
}

/**
 * A one-line, content-free summary for breadcrumbs.
 *
 * Rule ids and closed reason codes only — no ticket, no customer, no message.
 */
export function summariseInvariantCoverage(coverage: InvariantCoverage): {
  complete: boolean;
  checked: InvariantRuleId[];
  skipped: Array<{
    rule: InvariantRuleId; status: InvariantCoverageStatus;
    reason?: InvariantSkipReason; missingFields?: readonly InvariantMissingFields[];
  }>;
  /** CORE-BQ Unit 6. Population truth, alongside execution truth. */
  scope: {
    /** Every executed rule ran over a population the caller vouched for. */
    complete: boolean;
    /** Executed AND scope-complete — the rules this audit can speak for. */
    scopeComplete: InvariantRuleId[];
    filtered: Array<{ rule: InvariantRuleId; datasets: readonly InvariantRuleDatasetScope[] }>;
    unknown: InvariantRuleId[];
  };
} {
  return {
    complete: coverage.complete,
    checked: coverage.checks.filter((c) => isCompleteStatus(c.status)).map((c) => c.rule),
    skipped: skippedInvariantRules(coverage).map((c) => ({
      rule: c.rule, status: c.status, ...(c.reason ? { reason: c.reason } : {}),
      ...(c.missingFields ? { missingFields: c.missingFields } : {}),
    })),
    scope: {
      complete: coverage.checks.every((c) => c.rowScope ? c.rowScope.complete : true),
      scopeComplete: scopeCompleteRules(coverage).map((c) => c.rule),
      // Dataset names and closed reason codes only — the same content-free
      // discipline as the rest of this summary.
      filtered: filteredScopeRules(coverage)
        .map((c) => ({ rule: c.rule, datasets: c.rowScope!.datasets })),
      unknown: unknownScopeRules(coverage).map((c) => c.rule),
    },
  };
}
